// Import Dependencies
import PropTypes from "prop-types";
import { forwardRef } from "react";
import clsx from 'clsx'

// Local Imports
import { COLORS } from "constants/app.constant";
import { setThisClass } from "utils/setThisClass";

// ----------------------------------------------------------------------

/**
 * Button variant styles for themed colors
 */
const variants = {
  filled: clsx(
    "bg-primary-600 text-white",
    "hover:bg-primary-700 focus:bg-primary-700 active:bg-primary-800",
    "disabled:bg-primary-400 dark:bg-primary-500",
    "dark:hover:bg-primary-600 dark:focus:bg-primary-600 dark:disabled:bg-primary-700"
  ),
  soft: clsx(
    "text-primary-700 bg-primary-600/[.08]",
    "hover:bg-primary-600/[.15] focus:bg-primary-600/[.15] active:bg-primary-600/20",
    "dark:bg-primary-400/10 dark:text-primary-400",
    "dark:hover:bg-primary-400/20 dark:focus:bg-primary-400/20 dark:active:bg-primary-400/25"
  ),
  outlined: clsx(
    "text-primary-700 border border-primary-700",
    "hover:bg-primary-700/[.05] focus:bg-primary-700/[.05] active:bg-primary-700/10",
    "dark:border-primary-400 dark:text-primary-400",
    "dark:hover:bg-primary-400/[.05] dark:focus:bg-primary-400/[.05] dark:active:bg-primary-400/10"
  ),
  flat: clsx(
    "text-primary-700",
    "hover:bg-primary-700/[.08] focus:bg-primary-700/[.08] active:bg-primary-700/[.15]",
    "dark:text-primary-400",
    "dark:hover:bg-primary-400/10 dark:focus:bg-primary-400/10 dark:active:bg-primary-400/[.15]"
  ),
};

/**
 * Button variant styles for neutral (gray) colors
 */
const neutralVariants = {
  filled: clsx(
    "bg-gray-150 text-gray-900",
    "hover:bg-gray-200 focus:bg-gray-200 active:bg-gray-200/80",
    "dark:bg-surface-2 dark:text-dark-50",
    "dark:hover:bg-surface-1 dark:focus:bg-surface-1 dark:active:bg-surface-1/90"
  ),
  soft: clsx(
    "bg-gray-150/30 text-gray-900",
    "hover:bg-gray-200/[.15] focus:bg-gray-200/[.15] active:bg-gray-200/20",
    "dark:bg-dark-500/30 dark:text-dark-50",
    "dark:hover:bg-dark-450/[.15] dark:focus:bg-dark-450/[.15] dark:active:bg-dark-450/20"
  ),
  outlined: clsx(
    "border border-gray-300 text-gray-900",
    "hover:bg-gray-300/20 focus:bg-gray-300/20 active:bg-gray-300/25",
    "dark:text-dark-50 dark:border-dark-450",
    "dark:hover:bg-dark-300/20 dark:focus:bg-dark-300/20 dark:active:bg-dark-300/25"
  ),
  flat: clsx(
    "text-gray-700",
    "hover:bg-gray-300/20 focus:bg-gray-300/20 active:bg-gray-300/25",
    "dark:text-dark-200",
    "dark:hover:bg-dark-300/10 dark:focus:bg-dark-300/10 dark:active:bg-dark-300/20"
  ),
};

const Button = forwardRef((props, ref) => {
  const {
    component,
    className,
    children,
    color,
    isIcon,
    variant = "filled",
    unstyled,
    type = "button",
    isGlow,
    disabled,
    onClick,
    ...rest
  } = props;

  const Component = component || "button";
  const mergedColor = color || "neutral";

  return (
    <Component
      className={clsx(
        "btn-base",
        !unstyled
          ? [
              "btn",
              isIcon && "shrink-0 p-0",
              mergedColor === "neutral"
                ? [
                    neutralVariants[variant],
                    isGlow &&
                      "shadow-lg shadow-gray-200/50 dark:shadow-dark-450/5",
                  ]
                : [
                    setThisClass(mergedColor),
                    variants[variant],
                    isGlow &&
                      "shadow-soft shadow-this/50 dark:shadow-lg dark:shadow-this/50",
                  ],
            ]
          : color && color !== "neutral" && setThisClass(color),
        className,
      )}
      type={type}
      ref={ref}
      disabled={disabled}
      data-disabled={disabled}
      onClick={onClick}
      {...rest}
    >
      {children}
    </Component>
  );
});

Button.displayName = "Button";

Button.propTypes = {
  children: PropTypes.node,
  component: PropTypes.elementType,
  className: PropTypes.string,
  type: PropTypes.string,
  isIcon: PropTypes.bool,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  color: PropTypes.oneOf(COLORS),
  variant: PropTypes.oneOf(["filled", "outlined", "soft", "flat"]),
  unstyled: PropTypes.bool,
  isGlow: PropTypes.bool,
};

export { Button };
